import {useRef, useContext, useEffect, useState, Fragment} from "react";
import {
  Switch,
  Route,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import {ToastContainer} from "react-toastify";

import AuthContext from "./context/AuthContext";
// import SongContextProvider from "./context/SongContextProvider";
import PlayerContextProvider from "./context/ContextProvider";

import Layout from "./components/Layout/Layout";
import Header from "./components/Layout/Header/Header";
import Content from "./components/Layout/Content/Content";
import Navigation from "./components/Navigation/Navigation";

// import Loading from "./components/UI/Loading/Loading";
import Spinner from "./components/UI/Loading/Spinner";
// import SearchNavigation from "./components/UI/SearchNavigation/SearchNavigation";

import HomePage from "./pages/Home/Home";
import GenresPage from "./pages/Genres/Genres";
import SearchPage from "./pages/Search/Search";
import BrowsePage from "./pages/Browse/Browse";
// import SongPage from "./pages/Song/Song";
import SongPageRenderer from "./pages/Song/SongPageRenderer";
import FavouritesPage from "./pages/Home/Favourites";

import SignupPage from "./pages/Auth/Signup";
import LoginPage from "./pages/Auth/Login";
import SettingsPage from "./pages/Settings/Settings";

import SongWrapper from "./pages/Song/SongWrapper";
import Popup from "./components/UI/Popup/Popup";
import DebugConsole from "./components/DebugConsole/DebugConsole";

const App = () => {
  const rootElementRef = useRef();

  const [previousPageUrl, setPreviousPageUrl] = useState(null);

  const [initialUrl, setInitialUrl] = useState(null);

  const [toastPosition, setToastPosition] = useState("bottom-center");

  const [isLoggingin, setIsLoggingin] = useState(false);

  const location = useLocation();

  const history = useHistory();

  const AuthCtx = useContext(AuthContext);
  const {
    username,
    avatarUrl,
    token,
    isAuth,
    login,
    logout,
    authLoading,
    editAuthConf,
    setIsAuthLoading,
    setAutoLogout,
    initLibrary,
    // pushToLibrary,
    // removeFromLibrary,
    // library,
    addToFavouritesHandler,
    removeFromFavouritesHandler,

    topBoxMessage,
    setTopBoxMessage,
    language,
    country,
  } = AuthCtx;

  useEffect(() => {
    const unsubscribe = history.listen(location => {
      if (!location.pathname.startsWith("/play")) {
        setPreviousPageUrl(location.pathname + location.search);
      }
    });

    return () => unsubscribe();
  }, [history]);

  useEffect(() => {
    const blockedPathname =
      location.pathname.includes("/login") ||
      location.pathname.includes("/signup");
    const url =
      (blockedPathname ? "/home" : location.pathname) +
        (blockedPathname ? "" : location.search);
    setInitialUrl(url);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;

    const checkIsAuth = async () => {
      try {
        if (cancelled) return;
        setIsLoggingin(false);
        await login();
        if (cancelled) return;
        setIsLoggingin(true);
      } catch (error) {
        if (cancelled) return;
        setIsLoggingin(true);
      }
    };

    checkIsAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Layout ref={rootElementRef}>
      <Switch>
        <Route path="/signup">
          {isAuth ? (
            <Redirect to="/home" />
          ) : (
            <SignupPage
              authLoading={authLoading}
              editAuthConf={editAuthConf}
              setAuthLoading={setIsAuthLoading}
              topBoxMessage={topBoxMessage}
              setTopBoxMessage={setTopBoxMessage}
            />
          )}
        </Route>

        <Route path="/login">
          {isAuth ? (
            <Redirect to="/home" />
          ) : (
            <LoginPage
              authLoading={authLoading}
              editAuthConf={editAuthConf}
              setAuthLoading={setIsAuthLoading}
              setAutoLogout={setAutoLogout}
              topBoxMessage={topBoxMessage}
              setTopBoxMessage={setTopBoxMessage}
            />
          )}
        </Route>

        {isAuth ? (
          <Route path="/">
            <Header>
              <Navigation logout={logout} previousPageUrl={previousPageUrl} />
            </Header>
            <Content>
              <PlayerContextProvider>
                <Switch>
                  <Redirect exact to="/home" from="/" />
                  <Route path="/home">
                    <HomePage
                      country={country}
                      language={language}
                      logout={logout}
                      token={token}
                      rootElementRef={rootElementRef}
                      username={username}
                      avatarUrl={avatarUrl}
                    />
                  </Route>

                  <Route path="/favourites">
                    <FavouritesPage
                      logout={logout}
                      removeFromFavouritesHandler={removeFromFavouritesHandler}
                      initLibrary={initLibrary}
                      token={token}
                      rootElementRef={rootElementRef}
                      language={language}
                    />
                  </Route>

                  <Route path="/genres">
                    <GenresPage
                      country={country}
                      language={language}
                      logout={logout}
                      token={token}
                      rootElementRef={rootElementRef}
                    />
                  </Route>

                  <Route path="/settings">
                    <SettingsPage
                      logout={logout}
                      token={token}
                      rootElementRef={rootElementRef}
                    />
                  </Route>

                  <Redirect
                    exact
                    path="/search/:searchQuery"
                    to="/search/:searchQuery/songs"
                  />

                  <Route path="/search/:searchQuery/:searchTerm">
                    <SearchPage
                      logout={logout}
                      token={token}
                      rootElementRef={rootElementRef}
                    />
                  </Route>

                  <Route path="/browse/:browseId">
                    <BrowsePage
                      logout={logout}
                      addToFavouritesHandler={addToFavouritesHandler}
                      removeFromFavouritesHandler={removeFromFavouritesHandler}
                      token={token}
                      rootElementRef={rootElementRef}
                    />
                  </Route>

                  <Route path="/play">
                    <SongPageRenderer rootElementRef={rootElementRef} />
                  </Route>
                  
                  <Redirect to="/home" />
                </Switch>
                <SongWrapper
                  logout={logout}
                  addToFavouritesHandler={addToFavouritesHandler}
                  removeFromFavouritesHandler={removeFromFavouritesHandler}
                  setToastPosition={setToastPosition}
                  token={token}
                  previousPageUrl={previousPageUrl}
                />
              </PlayerContextProvider>
            </Content>
          </Route>
        ) : (
          <Redirect to="/login" />
        )}
      </Switch>

      <Popup
        notCloseable
        autoClose={isLoggingin === true}
        content={
          <Fragment>
            <p className="link-large flex-center mg-t-3">
              Logging in
              <Spinner className="mg-l-2" />
            </p>
          </Fragment>
        }
      />

      <ToastContainer
        limit={4}
        position={toastPosition}
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick={true}
        pauseOnHover={false}
        rtl={false}
        draggable
      />
      <DebugConsole />
    </Layout>
  );
};

export default App;

